import { AppSelect } from "../AppSelect";
import React, { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Layers3, ChevronRight, Clock3, AppWindow, Route, Search, Users, X } from "lucide-react";
import type { Recording } from "../../api/types";
import { formatDuration, parseUtcDate } from "../../utils";
import {
  activityBins,
  activitySeconds,
  activityBucketDays,
  aggregateActivityBins,
} from "./activityModel";
import ActivityHistogram from "./ActivityHistogram";
import ActivityHistory from "./ActivityHistory";
import ActivityAllocation from "./ActivityAllocation";
import { useProcessList } from "./useProcessOverview";
import { EMPTY_PROCESS_OVERVIEW, summarizeProcessOverview } from "./processOverview";
import { personSeconds, processPeople, summariesForPeople } from "../processes/processPeople";
import ActivityMetric from "./ActivityMetric";
import RecentSessions from "./RecentSessions";
import { getBrowserHash, splitHash, updateHash } from "../../utils/urlHash";
import { useTeamActivityFilters } from "./useActivityFilters";
import DisplayMenu, { displayPillClass } from "../DisplayMenu";
import TabBandButton from "../TabBandButton";
import { getPersonColor, personInitials } from "../analytics/palette";
import LatestSessionBanner from "./LatestSessionBanner";
import {
  DEFAULT_TEAM_COLUMNS,
  TEAM_COLUMNS,
  type TeamColumn,
  persistTeamColumns,
  readTeamColumns,
} from "./teamColumns";

export const TEAM_MEMBER_HASH_KEY = "member";
export const MEMBER_VIEW_HASH_KEY = "memberView";

type MemberView = "work" | "sessions";

type Coverage = "available" | "low" | "none";

const COLUMNS = {
  capturedTime: { label: "activity.captured_time", width: "w-52" },
  processTime: { label: "activity.process_time", width: "w-32" },
  sessions: { label: "activity.sessions_short", width: "w-24" },
  processes: { label: "activity.processes", width: "w-24" },
  daily: { label: "activity.daily", width: "w-[22%]" },
  coverage: { label: "activity.coverage", width: "w-36" },
  lastCapture: { label: "activity.last_capture", width: "w-36" },
} as const satisfies Record<TeamColumn, { label: string; width: string }>;
const COLUMN_TITLES: Partial<
  Record<TeamColumn, "activity.process_time_hint" | "activity.distinct_work_counts">
> = {
  processTime: "activity.process_time_hint",
  processes: "activity.distinct_work_counts",
};

const formatShare = (part: number, total: number) => {
  const share = total ? part / total : 0;
  return share > 0 && share < 0.01 ? "<1%" : `${Math.round(share * 100)}%`;
};

function CoverageBadge({ coverage }: { coverage: Coverage }) {
  const { t } = useTranslation();
  return (
    <span className="inline-flex shrink-0 items-center gap-2 whitespace-nowrap text-xs text-text-mid">
      <span
        aria-hidden="true"
        className={`h-2 w-2 rounded-full ${coverage === "available" ? "bg-success" : coverage === "low" ? "bg-warning" : "border border-outline-strong"}`}
      />
      {t(
        coverage === "available"
          ? "activity.data_present"
          : coverage === "low"
            ? "activity.low_data"
            : "activity.no_data",
      )}
    </span>
  );
}

// Same identity colour and initials as the person groups on Processes.
function PersonAvatar({ id, label, size }: { id: string; label: string; size: "sm" | "md" }) {
  const { i18n } = useTranslation();
  return (
    <span
      aria-hidden="true"
      className={`flex shrink-0 items-center justify-center rounded-full text-xs font-semibold text-white ${size === "sm" ? "h-8 w-8" : "h-9 w-9"}`}
      style={{ backgroundColor: getPersonColor(id) }}
    >
      {personInitials(label, i18n.resolvedLanguage)}
    </span>
  );
}

export default function TeamActivity({
  recordings,
  archivedRecordings = [],
  people,
  from,
  to,
  coveragePeriod,
  revision,
}: {
  recordings: Recording[];
  archivedRecordings?: Recording[];
  people: Array<{ id: string; label: string }>;
  from: string;
  to: string;
  coveragePeriod: { start: number; end: number };
  revision: number;
}) {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { query, sort, onChange: changeFilters } = useTeamActivityFilters();
  const [columns, setColumns] = useState(readTeamColumns);
  const changeColumns = (next: readonly TeamColumn[]) => {
    setColumns(next);
    persistTeamColumns(next);
  };
  const hashParams = splitHash(location.hash).params;
  const selectedId = hashParams.get(TEAM_MEMBER_HASH_KEY);
  const memberView: MemberView =
    hashParams.get(MEMBER_VIEW_HASH_KEY) === "sessions" ? "sessions" : "work";
  // One list for the whole team: each person's work, and the selected member's, add up from it.
  const team = useProcessList(
    people.map(person => person.id),
    from,
    to,
    true,
    revision,
  );
  const teamProcesses = team.data;
  const personWork = useMemo(() => {
    const work = new Map<string, { processes: number; seconds: number }>();
    for (const process of teamProcesses ?? []) {
      for (const person of processPeople(process)) {
        const row = work.get(person.userId) ?? { processes: 0, seconds: 0 };
        row.processes += 1;
        row.seconds += personSeconds(person);
        work.set(person.userId, row);
      }
    }
    return work;
  }, [teamProcesses]);
  // Every member shares one calendar axis, including people without captures and All time.
  const calendar = useMemo(
    () => activityBins(recordings, from || undefined, to || undefined),
    [recordings, from, to],
  );
  const calendarFrom = calendar[0] ? new Date(calendar[0].start).toISOString() : undefined;
  const calendarTo = calendar.length
    ? new Date(calendar[calendar.length - 1].start).toISOString()
    : undefined;
  const rows = useMemo(
    () =>
      people.map(person => {
        const sessions = recordings
          .filter(item => item.user_id === person.id)
          .sort(
            (a, b) =>
              parseUtcDate(b.date_started).getTime() - parseUtcDate(a.date_started).getTime(),
          );
        const bins = activityBins(sessions, calendarFrom, calendarTo);
        const seconds = activitySeconds(sessions, coveragePeriod.start, coveragePeriod.end);
        const elapsedSeconds = Math.max(0, coveragePeriod.end - coveragePeriod.start) / 1000;
        const dataCoverage: Coverage =
          seconds === 0 ? "none" : seconds < elapsedSeconds * 0.01 ? "low" : "available";
        return {
          ...person,
          sessions,
          seconds,
          dataCoverage,
          bins,
          days: bins.filter(bin => bin.seconds > 0).length,
        };
      }),
    [people, recordings, calendarFrom, calendarTo, coveragePeriod.start, coveragePeriod.end],
  );
  const observed = rows.filter(row => row.seconds > 0).length;
  const capturedTotal = rows.reduce((sum, row) => sum + row.seconds, 0);
  const latest = recordings.reduce<Recording | undefined>(
    (newest, item) =>
      !newest || parseUtcDate(item.date_started) > parseUtcDate(newest.date_started)
        ? item
        : newest,
    undefined,
  );
  const selected = rows.find(row => row.id === selectedId);
  const selectedMemberId = selected?.id;
  const work = useMemo(
    () =>
      selectedMemberId && teamProcesses
        ? summarizeProcessOverview(summariesForPeople(teamProcesses, [selectedMemberId]))
        : EMPTY_PROCESS_OVERVIEW,
    [selectedMemberId, teamProcesses],
  );
  const countsAvailable = !team.loading && !team.failed && !!teamProcesses;
  const visible = rows
    .map(row => {
      const counts = personWork.get(row.id);
      return {
        ...row,
        processes: countsAvailable ? (counts?.processes ?? 0) : null,
        processSeconds: countsAvailable ? (counts?.seconds ?? 0) : null,
      };
    })
    .filter(row => row.label.toLowerCase().includes(query.toLowerCase()))
    .sort((a, b) =>
      sort === "name"
        ? a.label.localeCompare(b.label)
        : sort === "processes"
          ? (b.processes ?? -1) - (a.processes ?? -1) || a.label.localeCompare(b.label)
          : sort === "process_time"
            ? (b.processSeconds ?? -1) - (a.processSeconds ?? -1) || a.label.localeCompare(b.label)
            : sort === "sessions"
              ? b.sessions.length - a.sessions.length || a.label.localeCompare(b.label)
              : sort === "recent"
                ? (parseUtcDate(b.sessions[0]?.date_started ?? "").getTime() || 0) -
                  (parseUtcDate(a.sessions[0]?.date_started ?? "").getTime() || 0)
                : b.seconds - a.seconds,
    );
  const displayModified = sort !== "time" || columns.join() !== DEFAULT_TEAM_COLUMNS.join();
  const bucketDays = activityBucketDays(calendar.length, 24);
  const chartBins = new Map(rows.map(row => [row.id, aggregateActivityBins(row.bins, bucketDays)]));
  const dailyPeak = Math.max(
    1,
    ...[...chartBins.values()].flatMap(bins => bins.map(bin => bin.seconds)),
  );
  const selectedBins = selected?.bins ?? [];
  const date = (value: number) =>
    new Date(value).toLocaleDateString(i18n.resolvedLanguage, {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  const selectPerson = (id: string | null, view?: MemberView) => {
    if (id === selectedId && (!view || view === memberView)) return;
    // Filters can replace the live hash without notifying the router. Preserve their latest
    // values, but push member changes so Back/Forward can restore the previous open panel.
    navigate({
      pathname: location.pathname,
      search: location.search,
      hash: updateHash(getBrowserHash(), {
        [TEAM_MEMBER_HASH_KEY]: id,
        ...(view && { [MEMBER_VIEW_HASH_KEY]: view === "work" ? null : view }),
      }),
    });
  };
  // The chosen view outlives the person, so the next member opens straight into it.
  const changeMemberView = (view: MemberView) =>
    navigate(
      {
        pathname: location.pathname,
        search: location.search,
        hash: updateHash(getBrowserHash(), {
          [MEMBER_VIEW_HASH_KEY]: view === "work" ? null : view,
        }),
      },
      { replace: true, state: location.state },
    );
  const control =
    "rounded-lg border border-outline bg-surface px-3 py-2 text-sm text-text-mid focus-visible:outline-info";
  return (
    <div className="@container space-y-5">
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <ActivityMetric
          label={t("activity.people_with_data")}
          value={`${observed} / ${people.length}`}
          icon={<Users size={18} />}
        />
        <ActivityMetric
          label={t("activity.sessions")}
          value={recordings.length}
          icon={<Layers3 size={18} />}
        />
        <ActivityMetric
          label={t("activity.captured_time")}
          value={formatDuration(capturedTotal)}
          icon={<Clock3 size={18} />}
        />
        <ActivityMetric
          label={t("activity.processes")}
          value={team.loading || team.failed ? null : (teamProcesses?.length ?? 0)}
          icon={<Route size={18} />}
        />
      </div>
      {team.failed && (
        <p role="alert" className="text-sm text-danger">
          {t("dashboard.load_failed")}{" "}
          <button onClick={team.reload} className="underline">
            {t("retry")}
          </button>
        </p>
      )}
      {latest && (
        <LatestSessionBanner
          recording={latest}
          person={people.find(person => person.id === latest.user_id)?.label ?? ""}
          revision={revision}
        />
      )}
      <div
        className={`grid overflow-clip rounded-xl border border-outline bg-surface ${selected ? "@[760px]:grid-cols-[264px_minmax(0,1fr)]" : ""}`}
      >
        <section
          className={`min-w-0 ${selected ? "border-b border-outline bg-muted-surface/20 @[760px]:border-b-0 @[760px]:border-r" : ""}`}
        >
          <div className={selected ? "sticky top-0" : ""}>
            <div
              className={`flex flex-wrap items-center justify-between gap-3 ${selected ? "p-3" : "p-5"}`}
            >
              <div>
                <h2 className="text-lg font-semibold">{t("activity.team_members")}</h2>
                {!selected && (
                  <p className="mt-1 text-sm text-text-dim">{t("activity.members_hint")}</p>
                )}
              </div>
              <div className={`flex flex-wrap gap-2 ${selected ? "w-full" : ""}`}>
                <label className={`relative ${selected ? "w-full" : ""}`}>
                  <Search className="absolute left-3 top-2.5 text-text-dim" size={16} />
                  <input
                    aria-label={t("activity.search_people")}
                    placeholder={t("activity.search_people")}
                    className={`${control} ${selected ? "w-full" : "w-52"} pl-9`}
                    value={query}
                    onChange={event => changeFilters({ query: event.target.value })}
                  />
                </label>
                {!selected && (
                  <DisplayMenu
                    modified={displayModified}
                    onReset={() => {
                      changeFilters({ sort: "time" });
                      changeColumns(DEFAULT_TEAM_COLUMNS);
                    }}
                  >
                    <div className="grid grid-cols-[auto_minmax(0,1fr)] items-center gap-x-3 text-sm text-text-mid">
                      <span>{t("process_directory.sort_by")}</span>
                      <AppSelect
                        label={t("activity.sort")}
                        value={sort}
                        onChange={sort => changeFilters({ sort })}
                        choices={[
                          { id: "time", display: t("activity.captured_time") },
                          { id: "process_time", display: t("activity.process_time") },
                          { id: "processes", display: t("activity.processes") },
                          { id: "sessions", display: t("activity.sessions_short") },
                          { id: "recent", display: t("activity.last_capture") },
                          { id: "name", display: t("activity.name") },
                        ]}
                      />
                    </div>
                    <fieldset className="mt-3 border-t border-outline pt-3">
                      <legend className="mb-2 text-sm text-text-mid">
                        {t("process_directory.columns")}
                      </legend>
                      <div className="flex flex-wrap gap-1.5">
                        {TEAM_COLUMNS.map(column => (
                          <button
                            key={column}
                            type="button"
                            aria-pressed={columns.includes(column)}
                            onClick={() =>
                              changeColumns(
                                TEAM_COLUMNS.filter(c => (c === column) !== columns.includes(c)),
                              )
                            }
                            className={displayPillClass(columns.includes(column))}
                          >
                            {t(COLUMNS[column].label)}
                          </button>
                        ))}
                      </div>
                    </fieldset>
                  </DisplayMenu>
                )}
              </div>
            </div>
            {selected ? (
              <div
                className="max-h-48 overflow-y-auto p-2 pt-0 @[760px]:max-h-[calc(100vh-260px)]"
                aria-label={t("activity.team_members")}
              >
                {visible.map(row => (
                  <button
                    key={row.id}
                    type="button"
                    onClick={() => selectPerson(selectedId === row.id ? null : row.id)}
                    aria-pressed={selectedId === row.id}
                    className={`flex w-full items-center gap-2.5 rounded-lg px-2 py-3 text-left text-sm focus-visible:outline-info ${selectedId === row.id ? "bg-info/10 font-semibold text-info-strong" : "hover:bg-muted-surface"}`}
                  >
                    <PersonAvatar id={row.id} label={row.label} size="sm" />
                    <span className="min-w-0">
                      <span className="block break-words">{row.label}</span>
                      <span className="mt-1 block text-xs font-normal text-text-dim">
                        {t("activity.day_captured", { time: formatDuration(row.seconds) })}
                      </span>
                    </span>
                  </button>
                ))}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[960px] table-fixed whitespace-nowrap text-left text-sm">
                  <colgroup>
                    <col />
                    {columns.map(column => (
                      <col key={column} className={COLUMNS[column].width} />
                    ))}
                  </colgroup>
                  <thead className="bg-muted-surface/60 text-xs text-text-dim">
                    <tr>
                      {(["name", ...columns] as const).map(key => (
                        <th
                          key={key}
                          className="px-4 py-3 font-medium"
                          title={
                            key === "name" ? undefined : COLUMN_TITLES[key] && t(COLUMN_TITLES[key])
                          }
                        >
                          {t(key === "name" ? "activity.name" : COLUMNS[key].label)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {visible.map(row => (
                      <tr
                        key={row.id}
                        onClick={() => selectPerson(row.id)}
                        className="cursor-pointer border-t border-outline/70 hover:bg-muted-surface/40"
                      >
                        <th scope="row" className="px-4 py-4">
                          <button
                            onClick={event => {
                              event.stopPropagation();
                              selectPerson(row.id);
                            }}
                            aria-expanded={selectedId === row.id}
                            className="flex items-center gap-3 rounded text-left font-medium focus-visible:outline-info"
                          >
                            <PersonAvatar id={row.id} label={row.label} size="md" />
                            <span className="min-w-0 whitespace-normal break-words">
                              {row.label}
                            </span>
                            <ChevronRight size={14} className="shrink-0 text-text-dim" />
                          </button>
                        </th>
                        {columns.includes("capturedTime") && (
                          <td className="px-4 py-4">
                            <span className="tabular-nums">
                              {formatDuration(row.seconds)}{" "}
                              <span className="text-text-dim">
                                ({formatShare(row.seconds, capturedTotal)})
                              </span>
                            </span>
                            <span
                              aria-hidden="true"
                              className="mt-2 block h-1.5 overflow-hidden rounded-full bg-muted-surface"
                            >
                              <span
                                className="block h-full rounded-full"
                                style={{
                                  backgroundColor: getPersonColor(row.id),
                                  width: `${capturedTotal ? (row.seconds / capturedTotal) * 100 : 0}%`,
                                }}
                              />
                            </span>
                          </td>
                        )}
                        {columns.includes("processTime") && (
                          <td className="px-4 py-4 tabular-nums" aria-busy={team.loading}>
                            {row.processSeconds === null ? "—" : formatDuration(row.processSeconds)}
                          </td>
                        )}
                        {columns.includes("sessions") && (
                          <td className="px-4 py-4 tabular-nums">
                            <button
                              type="button"
                              title={t("activity.show_sessions")}
                              onClick={event => {
                                event.stopPropagation();
                                selectPerson(row.id, "sessions");
                              }}
                              className="rounded text-info hover:underline focus-visible:outline-info"
                            >
                              {row.sessions.length}
                            </button>
                          </td>
                        )}
                        {columns.includes("processes") && (
                          <td className="px-4 py-4 tabular-nums" aria-busy={team.loading}>
                            {row.processes ?? "—"}
                          </td>
                        )}
                        {columns.includes("daily") && (
                          <td className="px-4 py-4">
                            <ActivityHistogram
                              compact
                              bins={chartBins.get(row.id) ?? []}
                              max={dailyPeak}
                              color={getPersonColor(row.id, 0.75)}
                            />
                          </td>
                        )}
                        {columns.includes("coverage") && (
                          <td className="px-4 py-4">
                            <CoverageBadge coverage={row.dataCoverage} />
                          </td>
                        )}
                        {columns.includes("lastCapture") && (
                          <td className="px-4 py-4 tabular-nums">
                            {row.sessions[0]
                              ? date(parseUtcDate(row.sessions[0].date_started).getTime())
                              : "—"}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {!visible.length && (
              <p className="p-5 text-sm text-text-dim">{t("no_results_match_filters")}</p>
            )}
          </div>
        </section>
        {selected && (
          <aside
            aria-label={selected.label}
            className="@container min-w-0 space-y-4 p-4 @[1000px]:p-5"
          >
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="flex flex-wrap items-center gap-2.5">
                <h2 className="text-lg font-semibold">{selected.label}</h2>
                <CoverageBadge coverage={selected.dataCoverage} />
              </div>
              <button
                type="button"
                onClick={() => selectPerson(null)}
                aria-label={t("activity.back_to_members")}
                title={t("activity.back_to_members")}
                className="rounded-lg p-2 text-text-mid transition-colors hover:bg-muted-surface hover:text-text focus-visible:outline-info"
              >
                <X className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-2 @[560px]:grid-cols-4">
              <ActivityMetric
                compact
                label={t("activity.captured_time")}
                value={formatDuration(selected.seconds)}
                detail={t("activity.across_days", { count: selected.days })}
                icon={<Clock3 size={15} />}
                tone="text-text-dim"
              />
              <ActivityMetric
                compact
                label={t("activity.sessions_short")}
                value={selected.sessions.length}
                detail={
                  selected.sessions[0] &&
                  t("activity.last_capture_on", {
                    date: date(parseUtcDate(selected.sessions[0].date_started).getTime()),
                  })
                }
                icon={<Layers3 size={15} />}
                tone="text-text-dim"
              />
              <ActivityMetric
                compact
                label={t("activity.processes")}
                value={team.loading || team.failed ? null : work.processes.length}
                icon={<Route size={15} />}
                tone="text-text-dim"
                title={t("activity.work_scope")}
              />
              <ActivityMetric
                compact
                label={t("activity.apps")}
                value={team.loading || team.failed ? null : work.appCount}
                icon={<AppWindow size={15} />}
                tone="text-text-dim"
                title={t("activity.work_scope")}
              />
            </div>

            <nav
              aria-label={t("activity.member_view")}
              className="flex gap-6 border-b border-outline"
            >
              <TabBandButton
                active={memberView === "work"}
                onClick={() => changeMemberView("work")}
                label={t("activity.work_view")}
                className="px-1 pb-2.5"
              />
              <TabBandButton
                active={memberView === "sessions"}
                onClick={() => changeMemberView("sessions")}
                label={t("activity.sessions_view")}
                count={selected.sessions.length}
                className="px-1 pb-2.5"
              />
            </nav>
            {memberView === "work" ? (
              <div aria-busy={team.loading}>
                <p className="mb-3 text-xs text-text-dim">{t("activity.work_scope")}</p>
                {team.loading ? (
                  <p
                    role="status"
                    className="rounded-xl border border-outline p-8 text-center text-sm text-text-dim"
                  >
                    {t("loading_ellipsis")}
                  </p>
                ) : team.failed ? (
                  <p
                    role="alert"
                    className="rounded-xl border border-outline p-8 text-center text-sm text-text-mid"
                  >
                    {t("activity.work_load_failed")}{" "}
                    <button type="button" onClick={team.reload} className="text-info underline">
                      {t("retry")}
                    </button>
                  </p>
                ) : (
                  <ActivityAllocation
                    key={`${selected.id}:${from}:${to}`}
                    metrics={work}
                    userId={selected.id}
                    from={from}
                    to={to}
                  />
                )}
              </div>
            ) : (
              <>
                <ActivityHistory
                  height={120}
                  key={`${selected.id}:${calendarFrom}:${calendarTo}`}
                  bins={selectedBins}
                  recordings={selected.sessions}
                />
                <RecentSessions
                  key={`${selected.id}:${from}:${to}`}
                  recordings={[
                    ...selected.sessions,
                    ...archivedRecordings.filter(item => item.user_id === selected.id),
                  ]}
                  revision={revision}
                />
              </>
            )}
          </aside>
        )}
      </div>
    </div>
  );
}
